/**
 * ConfidenceBar - Horizontal progress bar with percentage label
 *
 * Pattern from previous version screenshots:
 * - Horizontal bar showing confidence/progress
 * - Percentage label on right
 * - Color coded by level (green high, orange medium, red low)
 */

interface ConfidenceBarProps {
  /** Value from 0-100 */
  value: number;
  /** Show percentage label */
  showLabel?: boolean;
  /** Label position */
  labelPosition?: 'right' | 'inside' | 'below';
  /** Override automatic color */
  color?: 'green' | 'orange' | 'red' | 'blue' | 'auto';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional label text */
  label?: string;
  className?: string;
}

export function ConfidenceBar({
  value,
  showLabel = true,
  labelPosition = 'right',
  color = 'auto',
  size = 'md',
  label,
  className = '',
}: ConfidenceBarProps) {
  // Clamp value between 0 and 100
  const clampedValue = Math.max(0, Math.min(100, value));

  // Auto color based on value
  const getColor = () => {
    if (color !== 'auto') return color;
    if (clampedValue >= 80) return 'green';
    if (clampedValue >= 60) return 'orange';
    return 'red';
  };

  const barColor = getColor();

  const colorClasses: Record<string, { bg: string; fill: string }> = {
    green: { bg: 'bg-success-500/20', fill: 'bg-success-500' },
    orange: { bg: 'bg-warning-500/20', fill: 'bg-warning-500' },
    red: { bg: 'bg-danger-500/20', fill: 'bg-danger-500' },
    blue: { bg: 'bg-primary-500/20', fill: 'bg-primary-500' },
  };

  const sizeClasses: Record<string, string> = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  };

  const { bg, fill } = colorClasses[barColor];
  const heightClass = sizeClasses[size];

  return (
    <div className={`${className}`}>
      {label && labelPosition === 'below' && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-text-tertiary uppercase">{label}</span>
          {showLabel && (
            <span className="text-sm font-medium text-white">{clampedValue}%</span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className={`flex-1 ${bg} rounded-full overflow-hidden ${heightClass}`}>
          <div
            className={`${fill} ${heightClass} rounded-full transition-all duration-300`}
            style={{ width: `${clampedValue}%` }}
          />
        </div>

        {showLabel && labelPosition === 'right' && (
          <span className="text-sm font-medium text-white min-w-[3rem] text-right">
            {clampedValue}%
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * ConfidenceBarLabeled - With "CONFIANZA" header like in screenshots
 */
interface ConfidenceBarLabeledProps extends Omit<ConfidenceBarProps, 'label' | 'labelPosition'> {
  /** Header label (default: "CONFIANZA") */
  header?: string;
}

export function ConfidenceBarLabeled({
  header = 'CONFIANZA',
  value,
  ...props
}: ConfidenceBarLabeledProps) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-text-tertiary uppercase tracking-wide">{header}</span>
      </div>
      <ConfidenceBar value={value} {...props} />
    </div>
  );
}
